// src/pages/Timbrature.jsx
//
// Timbrature del tecnico: entrata, pausa pranzo, uscita. Ogni timbratura
// prende la posizione dal telefono; senza posizione si puo' timbrare solo
// con una nota. Chi si e' dimenticato inserisce l'orario a mano con una
// nota ("timbratura dimenticata"). Senza rete la timbratura resta in coda
// sul telefono e parte da sola quando la rete torna.
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authFetch } from '../utils/authFetch';
import { eErroreDiRete } from '../utils/rete';
import { Badge, Card } from '../components/ui';

const API_BASE = '/api';
const CODA_KEY = 'timbrature_coda';

const STEPS = [
  { type: 'entrata', label: 'Entrata', color: 'bg-emerald-600 hover:bg-emerald-700' },
  { type: 'pausa_inizio', label: 'Inizio pausa pranzo', color: 'bg-amber-500 hover:bg-amber-600' },
  { type: 'pausa_fine', label: 'Fine pausa pranzo', color: 'bg-amber-600 hover:bg-amber-700' },
  { type: 'uscita', label: 'Uscita', color: 'bg-sky-600 hover:bg-sky-700' },
];


const TYPE_LABEL = {
  entrata: 'Entrata',
  pausa_inizio: 'Inizio pausa',
  pausa_fine: 'Fine pausa',
  uscita: 'Uscita',
};

const FLAG_LABEL = {
  incompleta: 'giornata incompleta',
  in_ritardo: 'orario inserito a mano',
  senza_posizione: 'senza posizione',
  pausa_non_chiusa: 'pausa non chiusa',
  festivo: 'giorno non lavorativo',
};

export const fmtMin = (m) => {
  if (m === null || m === undefined) return '-';
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return `${h}h ${String(mm).padStart(2, '0')}m`;
};

const fmtDay = (day) => {
  if (!day) return '';
  const d = new Date(`${day}T00:00:00`);
  return d.toLocaleDateString('it-IT', { weekday: 'short', day: '2-digit', month: '2-digit' });
};

const localHM = (date = new Date()) =>
  `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;

const localDay = (date = new Date()) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

const nuovoId = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `t-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

function leggiCoda() {
  try {
    const raw = localStorage.getItem(CODA_KEY);
    const list = raw ? JSON.parse(raw) : [];
    return Array.isArray(list) ? list : [];
  } catch {
    return [];
  }
}

function scriviCoda(list) {
  try {
    localStorage.setItem(CODA_KEY, JSON.stringify(list));
  } catch {
    // niente storage: pazienza
  }
}

/** Posizione dal telefono. Rifiuta con { reason } se non arriva. */
function getPosition() {
  return new Promise((resolve, reject) => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      reject({ reason: 'localizzazione non disponibile su questo dispositivo' });
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) =>
        resolve({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
          capturedAt: new Date(pos.timestamp || Date.now()).toISOString(),
        }),
      (err) => {
        const reasons = {
          1: 'permesso di localizzazione negato',
          2: 'posizione non disponibile',
          3: 'tempo scaduto nel cercare la posizione',
        };
        reject({ reason: reasons[err.code] || err.message || 'posizione non disponibile' });
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 60000 }
    );
  });
}

export default function Timbrature() {
  const navigate = useNavigate();
  const role = localStorage.getItem('role') || '';
  const isManager = ['admin', 'segreteria', 'gestione'].includes(role);

  const [oggi, setOggi] = useState(null);
  const [storico, setStorico] = useState(null);
  const [coda, setCoda] = useState(leggiCoda);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [posStatus, setPosStatus] = useState('');

  // modale: nota (posizione mancante) o timbratura dimenticata
  const [modal, setModal] = useState(null); // { mode: 'nota'|'dimenticata', type, reason, position }
  const [form, setForm] = useState({ type: 'entrata', time: '', note: '' });

  const done = Object.fromEntries((oggi?.entries || []).map((e) => [e.type, e]));
  const nextIndex = STEPS.findIndex((s) => !done[s.type]);

  const loadOggi = useCallback(async () => {
    try {
      const res = await authFetch(`${API_BASE}/timbrature/oggi`);
      if (res && res.ok) setOggi(await res.json());
    } catch (err) {
      if (!eErroreDiRete(err)) setError('Errore nel caricamento');
    }
  }, []);

  const loadStorico = useCallback(async () => {
    try {
      const res = await authFetch(`${API_BASE}/timbrature/mie`);
      if (res && res.ok) setStorico(await res.json());
    } catch {
      // senza rete lo storico resta quello che c'era
    }
  }, []);

  // invio della coda offline
  const svuotaCoda = useCallback(async () => {
    const list = leggiCoda();
    if (!list.length) return;
    const rimaste = [];
    for (const item of list) {
      try {
        const res = await authFetch(`${API_BASE}/timbrature`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(item),
        });
        if (!res) {
          rimaste.push(item);
          continue;
        }
        if (res.ok || res.status === 409 || res.status === 400) {
          // salvata, oppure rifiutata per un motivo che non cambia riprovando
          if (!res.ok) {
            const data = await res.json().catch(() => null);
            setError(`Timbratura ${TYPE_LABEL[item.type]} delle ${item.time} non accettata: ${data?.error || ''}`);
          }
          continue;
        }
        rimaste.push(item);
      } catch {
        rimaste.push(item);
      }
    }
    scriviCoda(rimaste);
    setCoda(rimaste);
    if (rimaste.length < list.length) {
      setNotice('Timbrature in coda inviate.');
      loadOggi();
      loadStorico();
    }
  }, [loadOggi, loadStorico]);

  useEffect(() => {
    loadOggi();
    loadStorico();
    svuotaCoda();
    const onOnline = () => svuotaCoda();
    window.addEventListener('online', onOnline);
    const tick = setInterval(loadOggi, 5 * 60 * 1000);
    return () => {
      window.removeEventListener('online', onOnline);
      clearInterval(tick);
    };
  }, [loadOggi, loadStorico, svuotaCoda]);

  // ---- invio ----

  const invia = async (payload) => {
    setBusy(true);
    setError('');
    try {
      const res = await authFetch(`${API_BASE}/timbrature`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res) throw new TypeError('rete');
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        if (data?.code === 'POSITION_REQUIRED') {
          setModal({ mode: 'nota', type: payload.type, reason: payload.position?.reason || 'posizione non disponibile', position: null });
          return false;
        }
        throw new Error(data?.error || 'Timbratura non registrata');
      }
      setNotice(`${TYPE_LABEL[payload.type]} timbrata alle ${data.entry?.time || ''}.`);
      setModal(null);
      await loadOggi();
      loadStorico();
      return true;
    } catch (err) {
      if (eErroreDiRete(err)) {
        // niente rete: in coda con l'orario di adesso
        const item = {
          ...payload,
          clientId: payload.clientId || nuovoId(),
          source: 'offline',
          day: localDay(),
          time: payload.time || localHM(),
        };
        const list = [...leggiCoda(), item];
        scriviCoda(list);
        setCoda(list);
        setModal(null);
        setNotice(`Sei senza rete: ${TYPE_LABEL[payload.type]} delle ${item.time} resta in coda e parte da sola appena torna la connessione.`);
        return true;
      }
      setError(err.message);
      return false;
    } finally {
      setBusy(false);
    }
  };

  const timbra = async (type) => {
    setBusy(true);
    setError('');
    setNotice('');
    setPosStatus('Cerco la posizione...');
    let position = null;
    try {
      position = await getPosition();
      setPosStatus(`Posizione trovata (precisione ${Math.round(position.accuracy || 0)} m)`);
    } catch (e) {
      setPosStatus('');
      setBusy(false);
      setModal({ mode: 'nota', type, reason: e.reason, position: null });
      setForm((f) => ({ ...f, note: '' }));
      return;
    }
    await invia({ type, position, clientId: nuovoId() });
    setPosStatus('');
  };

  const confermaModal = async () => {
    if (!form.note.trim()) {
      setError('Scrivi una nota.');
      return;
    }
    if (modal.mode === 'nota') {
      await invia({
        type: modal.type,
        position: { reason: modal.reason },
        note: form.note.trim(),
        clientId: nuovoId(),
      });
      return;
    }
    // dimenticata: orario a mano, posizione di adesso se c'e'
    if (!form.time) {
      setError('Indica l\'orario.');
      return;
    }
    let position = { reason: 'timbratura inserita dopo' };
    try {
      position = await getPosition();
    } catch (e) {
      position = { reason: e.reason };
    }
    await invia({ type: form.type, time: form.time, note: form.note.trim(), position, clientId: nuovoId() });
  };

  const apriDimenticata = () => {
    const primo = STEPS.find((s) => !done[s.type]);
    setForm({ type: primo ? primo.type : 'uscita', time: '', note: '' });
    setModal({ mode: 'dimenticata' });
    setError('');
  };

  const summary = oggi?.summary;

  return (
    <div className="min-h-screen bg-gray-100 py-6 px-3 md:px-6 flex justify-center">
      <div className="w-full max-w-2xl space-y-4">
        <Card className="shadow-sm px-4 py-4 md:px-6">
          <div className="flex items-center justify-between gap-2">
            <div>
              <h1 className="text-xl md:text-2xl font-semibold text-gray-800">Timbrature</h1>
              <p className="text-xs md:text-sm text-gray-500">
                {oggi ? `${fmtDay(oggi.day)} · ore ${oggi.nowTime}` : 'Caricamento...'}
                {oggi && !oggi.workingDay && ' · giorno non lavorativo'}
              </p>
            </div>
            <div className="flex gap-2">
              {isManager && (
                <button onClick={() => navigate('/timbrature/gestione')} className="px-3 py-1.5 rounded-lg bg-indigo-100 text-indigo-700 text-xs font-medium hover:bg-indigo-200">
                  Controllo
                </button>
              )}
              <button onClick={() => navigate('/dashboard')} className="px-3 py-1.5 rounded-lg bg-gray-100 text-gray-700 text-xs font-medium hover:bg-gray-200">
                ← Dashboard
              </button>
            </div>
          </div>
        </Card>

        {coda.length > 0 && (
          <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-800 flex justify-between gap-2">
            <span>
              {coda.length} {coda.length === 1 ? 'timbratura in attesa' : 'timbrature in attesa'} di rete:{' '}
              {coda.map((c) => `${TYPE_LABEL[c.type]} ${c.time}`).join(', ')}
            </span>
            <button type="button" onClick={svuotaCoda} className="text-xs underline">riprova</button>
          </div>
        )}
        {error && (
          <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700 flex justify-between gap-2">
            <span>{error}</span>
            <button type="button" onClick={() => setError('')} className="text-xs underline">chiudi</button>
          </div>
        )}
        {notice && (
          <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 text-sm text-emerald-800 flex justify-between gap-2">
            <span>{notice}</span>
            <button type="button" onClick={() => setNotice('')} className="text-xs underline">chiudi</button>
          </div>
        )}

        {/* pulsanti di oggi */}
        <div className="rounded-lg bg-sky-50 border border-sky-200 px-3 py-2 text-xs text-sky-800">
        <strong>Pausa automatica attiva.</strong>{' '}
        Se non viene registrata manualmente,
        l'inizio pausa viene inserito alle 13:00
        e la fine pausa alle 14:00, utilizzando
        la posizione disponibile del dispositivo.
        </div>
        <Card className="shadow-sm p-4 md:p-6">
          <div className="grid gap-3 sm:grid-cols-2">
            {STEPS.map((s, i) => {
              const e = done[s.type];
              const isNext = i === nextIndex;
              return (
                <div key={s.type} className={`rounded-xl border p-3 ${e ? 'border-emerald-200 bg-emerald-50' : isNext ? 'border-gray-300 bg-white' : 'border-gray-200 bg-gray-50'}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <div className="text-sm font-semibold text-gray-800">{s.label}</div>
                      {e ? (
                        <div className="text-2xl font-bold text-emerald-700">{e.time}</div>
                      ) : (
                        <div className="text-xs text-gray-400">{isNext ? 'tocca a questa' : 'non ancora'}</div>
                      )}
                      {e && (
                        <div className="flex flex-wrap gap-1 mt-1">
                          {e.late && <Badge className="bg-amber-100 text-amber-700">orario a mano</Badge>}
                          {e.position?.missing && <Badge className="bg-red-100 text-red-700">senza posizione</Badge>}
                          {e.source === 'offline' && <Badge className="bg-gray-100 text-gray-600">inviata dopo</Badge>}
                          {e.source === 'automatico' && (<Badge className="bg-sky-100 text-sky-700">automatica</Badge>)}
                        </div>
                      )}
                    </div>
                    {!e && (
                      <button
                        type="button"
                        disabled={busy || !isNext}
                        onClick={() => timbra(s.type)}
                        className={`px-4 py-3 rounded-lg text-white text-sm font-semibold transition disabled:opacity-40 ${s.color}`}
                      >
                        Timbra
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {posStatus && <p className="mt-3 text-xs text-gray-500">{posStatus}</p>}

          {summary && (summary.entrata || summary.uscita) && (
            <div className="mt-4 flex flex-wrap gap-3 text-sm text-gray-700">
              <span>Ore lavorate: <strong>{fmtMin(summary.workedMinutes)}</strong></span>
              <span>Pausa: <strong>{fmtMin(summary.pauseMinutes)}</strong></span>
              {summary.overtimeMinutes > 0 && (
                <span className="text-indigo-700">Straordinario: <strong>{fmtMin(summary.overtimeMinutes)}</strong></span>
              )}
            </div>
          )}

          <div className="mt-4 flex flex-wrap items-center gap-2 text-xs text-gray-500">
            <span>Ti sei dimenticato di timbrare?</span>
            <button type="button" disabled={busy || nextIndex === -1} onClick={apriDimenticata} className="px-3 py-1.5 rounded-lg bg-gray-100 text-gray-700 font-medium hover:bg-gray-200 disabled:opacity-40">
              Inserisci l'orario con una nota
            </button>
          </div>
        </Card>

        {/* storico */}
        <Card className="shadow-sm p-4 md:p-6">
          <div className="flex items-center justify-between gap-2 mb-2">
            <h2 className="text-sm font-semibold text-gray-800">Ultime due settimane</h2>
            {storico?.totals && (
              <div className="text-xs text-gray-500">
                {fmtMin(storico.totals.workedMinutes)} lavorate
                {storico.totals.overtimeMinutes > 0 && <span className="text-indigo-700"> · {fmtMin(storico.totals.overtimeMinutes)} straordinario</span>}
              </div>
            )}
          </div>
          {!storico ? (
            <div className="text-xs text-gray-400">Caricamento...</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-xs">
                <thead>
                  <tr className="text-left text-gray-500 border-b">
                    <th className="py-1.5 pr-2">Giorno</th>
                    <th className="py-1.5 pr-2">Entrata</th>
                    <th className="py-1.5 pr-2">Pausa</th>
                    <th className="py-1.5 pr-2">Uscita</th>
                    <th className="py-1.5 pr-2">Ore</th>
                    <th className="py-1.5 pr-2">Straord.</th>
                    <th className="py-1.5">Note</th>
                  </tr>
                </thead>
                <tbody>
                  {[...storico.days].reverse().map((d) => {
                    const s = d.summary;
                    const vuoto = !d.entries.length;
                    if (vuoto && !d.workingDay && !d.leaveType) return null;
                    return (
                      <tr key={d.day} className={`border-b border-gray-100 ${vuoto ? 'text-gray-400' : 'text-gray-800'}`}>
                        <td className="py-1.5 pr-2 whitespace-nowrap">{fmtDay(d.day)}</td>
                        {d.leaveType && vuoto ? (
                          <td colSpan={6} className="py-1.5 italic">{d.leaveType}</td>
                        ) : vuoto ? (
                          <td colSpan={6} className="py-1.5 italic">{d.holiday ? 'chiusura aziendale' : 'nessuna timbratura'}</td>
                        ) : (
                          <>
                            <td className="py-1.5 pr-2">{s.entrata ? d.entries.find((e) => e.type === 'entrata')?.time : '-'}</td>
                            <td className="py-1.5 pr-2 whitespace-nowrap">
                              {s.pausaInizio ? d.entries.find((e) => e.type === 'pausa_inizio')?.time : '-'}
                              {s.pausaFine ? ` → ${d.entries.find((e) => e.type === 'pausa_fine')?.time}` : ''}
                            </td>
                            <td className="py-1.5 pr-2">{s.uscita ? d.entries.find((e) => e.type === 'uscita')?.time : '-'}</td>
                            <td className="py-1.5 pr-2 whitespace-nowrap">{fmtMin(s.workedMinutes)}</td>
                            <td className="py-1.5 pr-2 whitespace-nowrap text-indigo-700">{s.overtimeMinutes ? fmtMin(s.overtimeMinutes) : ''}</td>
                            <td className="py-1.5">
                              <div className="flex flex-wrap gap-1">
                                {s.flags.map((f) => (
                                  <Badge key={f} className="bg-amber-100 text-amber-700">{FLAG_LABEL[f] || f}</Badge>
                                ))}
                              </div>
                            </td>
                          </>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {/* modale */}
        {modal && (
          <div className="fixed inset-0 z-40 bg-black/40 flex items-end sm:items-center justify-center p-3" onClick={() => !busy && setModal(null)}>
            <Card className="shadow-xl p-4 w-full max-w-md space-y-3" onClick={(e) => e.stopPropagation()}>
              {modal.mode === 'nota' ? (
                <>
                  <h3 className="text-base font-semibold text-gray-800">Posizione non disponibile</h3>
                  <p className="text-sm text-gray-600">
                    Il telefono non ha dato la posizione ({modal.reason}). Puoi timbrare <strong>{TYPE_LABEL[modal.type]}</strong> lo stesso
                    scrivendo dove sei: la timbratura resta segnata "senza posizione".
                  </p>
                </>
              ) : (
                <>
                  <h3 className="text-base font-semibold text-gray-800">Timbratura dimenticata</h3>
                  <p className="text-sm text-gray-600">Indica cosa e a che ora, e spiega in due parole. Resta segnata come inserita a mano.</p>
                  <div className="grid grid-cols-2 gap-2">
                    <label className="text-xs text-gray-600">
                      Cosa
                      <select value={form.type} onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))} className="mt-0.5 w-full px-2 py-1.5 border rounded-lg text-sm bg-white">
                        {STEPS.filter((s) => !done[s.type]).map((s) => (
                          <option key={s.type} value={s.type}>{s.label}</option>
                        ))}
                      </select>
                    </label>
                    <label className="text-xs text-gray-600">
                      Ora
                      <input type="time" value={form.time} onChange={(e) => setForm((f) => ({ ...f, time: e.target.value }))} className="mt-0.5 w-full px-2 py-1.5 border rounded-lg text-sm bg-white" />
                    </label>
                  </div>
                </>
              )}
              <label className="block text-xs text-gray-600">
                Nota
                <textarea rows={3} value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} placeholder={modal.mode === 'nota' ? 'es. sono al cantiere di Perugia, GPS spento' : 'es. entrato alle 8 ma non ho timbrato'} className="mt-0.5 w-full px-2 py-1.5 border rounded-lg text-sm bg-white" />
              </label>
              {error && <div className="text-xs text-red-700">{error}</div>}
              <div className="flex gap-2 justify-end">
                <button type="button" disabled={busy} onClick={() => setModal(null)} className="px-3 py-1.5 rounded-lg bg-gray-100 text-gray-700 text-xs font-medium">Annulla</button>
                {modal.mode === 'nota' && (
                  <button type="button" disabled={busy} onClick={() => { setModal(null); timbra(modal.type); }} className="px-3 py-1.5 rounded-lg bg-sky-100 text-sky-700 text-xs font-medium">Riprova posizione</button>
                )}
                <button type="button" disabled={busy} onClick={confermaModal} className="px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-xs font-semibold disabled:opacity-50">
                  {busy ? 'Attendi...' : 'Timbra'}
                </button>
              </div>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
